import { z } from 'zod'

// Phone number validation for Argentina
export const phoneSchema = z
  .string()
  .min(10, 'El teléfono debe tener al menos 10 dígitos')
  .regex(/^\+?54\s?9?\s?\(?\d{3}\)?\s?\d{4}\s?\d{4}$|^\d{10}$/, 'Formato de teléfono inválido')

// Email validation
export const emailSchema = z.string().email('Correo electrónico inválido')

// Password validation
export const passwordSchema = z
  .string()
  .min(8, 'La contraseña debe tener al menos 8 caracteres')
  .max(50, 'La contraseña no puede exceder 50 caracteres')

// Name validation
export const nameSchema = z
  .string()
  .min(2, 'El nombre debe tener al menos 2 caracteres')
  .max(50, 'El nombre no puede exceder 50 caracteres')
  .regex(/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/, 'El nombre solo puede contener letras')

// Login form schema
export const loginSchema = z.object({
  identifier: z.string().min(1, 'Este campo es requerido'), // Can be email or phone
  password: passwordSchema,
  remember: z.boolean().optional(),
})

export type LoginFormData = z.infer<typeof loginSchema>

// Register form schema (with phone/email toggle)
export const registerSchema = z
  .object({
    email: z
      .string()
      .email('Correo electrónico inválido')
      .or(z.literal(''))
      .optional(),
    phoneNumber: z
      .string()
      .transform((val) => val.replace(/[^\d]/g, '')) // Remove formatting
      .refine((val) => val === '' || val.length >= 10, 'Número de teléfono inválido')
      .or(z.literal(''))
      .optional(),
    password: z
      .string()
      .min(8, 'La contraseña es demasiado corta')
      .max(20, 'La contraseña es demasiado larga')
      .regex(/\d/, 'La contraseña debe contener al menos un número'),
    repeatPassword: z.string(),
    toc: z.boolean().refine((val) => val === true, {
      message: 'Es necesario aceptar los términos y condiciones',
    }),
    loginWithPhone: z.boolean(),
  })
  .refine(
    (data) => {
      if (data.loginWithPhone === false) {
        return (data.email?.length || 0) >= 5
      }
      return true
    },
    {
      message: 'Debes completar el campo correo electrónico',
      path: ['email'],
    }
  )
  .refine(
    (data) => {
      if (data.loginWithPhone === true) {
        return (data.phoneNumber?.length || 0) >= 10
      }
      return true
    },
    {
      message: 'Debes completar el campo número de teléfono',
      path: ['phoneNumber'],
    }
  )
  .refine(
    (data) => {
      return data.password === data.repeatPassword
    },
    {
      message: 'Las contraseñas no coinciden',
      path: ['repeatPassword'],
    }
  )

export type RegisterFormData = z.infer<typeof registerSchema>

// Password Recovery schema
export const passwordRecoverySchema = z
  .object({
    email: z
      .string()
      .email('El correo electrónico no es válido')
      .or(z.literal('')),
    phoneNumber: z
      .string()
      .transform((val) => val.replace(/[^\d]/g, ''))
      .refine((val) => val === '' || val.length >= 10, 'Número de teléfono inválido')
      .or(z.literal('')),
    recoverWithPhone: z.boolean(),
  })
  .refine(
    (data) => {
      if (data.recoverWithPhone === false) {
        return (data.email?.length || 0) >= 5
      }
      return true
    },
    {
      message: 'Debes completar el campo correo electrónico',
      path: ['email'],
    }
  )
  .refine(
    (data) => {
      if (data.recoverWithPhone === true) {
        return (data.phoneNumber?.length || 0) >= 10
      }
      return true
    },
    {
      message: 'Debes completar el campo número de teléfono',
      path: ['phoneNumber'],
    }
  )

export type PasswordRecoveryFormData = z.infer<typeof passwordRecoverySchema>

// Password Reset schema
export const passwordResetSchema = z
  .object({
    password: z
      .string()
      .min(8, 'La contraseña es demasiado corta')
      .max(20, 'La contraseña es demasiado larga')
      .regex(/\d/, 'La contraseña debe contener al menos un número'),
    repeatPassword: z.string(),
    token: z.string(),
  })
  .refine(
    (data) => {
      return data.password === data.repeatPassword
    },
    {
      message: 'Las contraseñas no coinciden',
      path: ['repeatPassword'],
    }
  )

export type PasswordResetFormData = z.infer<typeof passwordResetSchema>
